import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, ShoppingBag, Palette, Sparkles, XCircle } from 'lucide-react';

export default function ExecutiveBriefing({ user }) {
  const navigate = useNavigate();
  const [accessDeniedDialog, setAccessDeniedDialog] = useState(false);
  const [deniedModule, setDeniedModule] = useState('');

  const briefingModules = [
    {
      title: 'Merchandise',
      description: 'Merchandise executive briefing and reports',
      icon: ShoppingBag,
      path: '/task-management/executive-briefing/merchandise',
      permission: 'task_weekly_progress' // Using same permission for now
    },
    {
      title: 'Fashion Design',
      description: 'Fashion design executive briefing and reports',
      icon: Palette,
      path: '/task-management/executive-briefing/fashion-design',
      permission: 'task_weekly_progress'
    },
    {
      title: 'Textile Design',
      description: 'Textile design executive briefing and reports',
      icon: Sparkles,
      path: '/task-management/executive-briefing/textile-design',
      permission: 'task_weekly_progress'
    }
  ];

  const handleModuleClick = (moduleName, path, requiredPermission) => {
    if (user && user[requiredPermission]) {
      navigate(path);
    } else {
      setDeniedModule(moduleName);
      setAccessDeniedDialog(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/task-management')}
              className="hover:bg-white"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-4xl font-bold">Executive Briefing Report</h1>
              <p className="text-gray-600 mt-2">Executive reports and briefings for key departments</p>
            </div>
          </div>
        </div>

        {/* Module Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {briefingModules.map((module, idx) => {
            const Icon = module.icon;
            return (
              <Card
                key={idx}
                className="hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-black"
                onClick={() => handleModuleClick(module.title, module.path, module.permission)}
              >
                <CardHeader className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-lg bg-black flex items-center justify-center">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <CardTitle className="text-xl">{module.title}</CardTitle>
                  <CardDescription className="text-sm mt-2">
                    {module.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Access Denied Dialog */}
      <Dialog open={accessDeniedDialog} onOpenChange={setAccessDeniedDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
            </div>
            <DialogTitle className="text-center text-2xl font-bold">Access Denied</DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              You do not have permission to access the {deniedModule} module.
              <br />
              <br />
              Please contact your administrator to request access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button
              onClick={() => setAccessDeniedDialog(false)}
              className="bg-black hover:bg-gray-800 text-white px-8"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
